import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Inbox, Phone, Mail, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "qualified", "closed"];

const StatusPill = ({ value, onChange }) => (
  <select
    data-testid={`lead-status-select`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`px-2 py-1 text-xs font-medium tracking-wide border ${
      value === "new" ? "bg-[#C5A059]/10 border-[#C5A059] text-[#7d6630]" :
      value === "contacted" ? "bg-blue-50 border-blue-300 text-blue-800" :
      value === "qualified" ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
      "bg-slate-100 border-slate-300 text-slate-700"
    }`}
  >
    {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
  </select>
);

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, qualified: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([api.get("/leads"), api.get("/leads/stats")]);
      setLeads(l.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline">Lead inbox</div>
          <h2 className="font-display text-2xl md:text-3xl font-bold mt-1">Quote requests</h2>
        </div>
        <button onClick={load} className="btn-ghost text-sm" data-testid="admin-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-slate-200 mb-10">
        {[
          ["Total", stats.total],
          ["New", stats.new],
          ["Contacted", stats.contacted],
          ["Qualified", stats.qualified],
          ["Closed", stats.closed],
        ].map(([label, v]) => (
          <div key={label} className="bg-white p-5" data-testid={`stat-${label.toLowerCase()}`}>
            <div className="overline">{label}</div>
            <div className="font-display text-3xl font-bold mt-2">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-px bg-slate-200">
        <div className="lg:col-span-7 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="p-4 overline">Name</th>
                  <th className="p-4 overline">Project</th>
                  <th className="p-4 overline">Status</th>
                  <th className="p-4 overline">Date</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-500" data-testid="leads-loading">Loading leads…</td></tr>
                )}
                {!loading && leads.length === 0 && (
                  <tr><td colSpan={5} className="p-12 text-center text-slate-500" data-testid="leads-empty">
                    <Inbox size={28} className="mx-auto text-slate-400" />
                    <div className="mt-3">No leads yet. Submit one via the Contact form to see it here.</div>
                  </td></tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className={`border-b border-slate-200 hover:bg-slate-50 cursor-pointer ${selected?.id === l.id ? "bg-slate-100" : ""}`}
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{l.name}</div>
                      <div className="text-xs text-slate-500">{l.email}</div>
                    </td>
                    <td className="p-4">
                      <div className="text-slate-800">{l.project_type}</div>
                      <div className="text-xs text-slate-500">{l.budget_range || "—"}</div>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <StatusPill value={l.status} onChange={(s) => updateStatus(l.id, s)} />
                    </td>
                    <td className="p-4 text-slate-600 text-xs">
                      {new Date(l.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => remove(l.id)} aria-label="Delete lead" className="text-slate-400 hover:text-red-600" data-testid={`delete-lead-${l.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-5 bg-white p-6 md:p-8" data-testid="lead-detail-panel">
          {!selected ? (
            <div className="h-full min-h-[300px] flex items-center justify-center text-slate-500 text-center">
              Select a lead to view full details.
            </div>
          ) : (
            <div>
              <div className="overline">Lead detail</div>
              <h2 className="font-display text-2xl font-bold mt-2">{selected.name}</h2>
              <div className="mt-1 text-sm text-slate-500">
                Received {new Date(selected.created_at).toLocaleString("en-GB")}
              </div>

              <div className="mt-6 space-y-3 text-sm">
                <div className="flex gap-3"><Mail size={16} className="text-[#C5A059] mt-0.5" /><a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></div>
                <div className="flex gap-3"><Phone size={16} className="text-[#C5A059] mt-0.5" /><a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></div>
              </div>

              <div className="grid grid-cols-3 gap-px bg-slate-200 mt-6">
                <div className="bg-white p-3">
                  <div className="overline text-[10px]">Project</div>
                  <div className="text-sm font-medium mt-1">{selected.project_type}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="overline text-[10px]">Budget</div>
                  <div className="text-sm font-medium mt-1">{selected.budget_range || "—"}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="overline text-[10px]">Timeline</div>
                  <div className="text-sm font-medium mt-1">{selected.timeline || "—"}</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="overline mb-2">Message</div>
                <div className="border border-slate-200 p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50">
                  {selected.message}
                </div>
              </div>

              <div className="mt-6 flex items-center gap-3">
                <div className="overline">Status</div>
                <StatusPill value={selected.status} onChange={(s) => updateStatus(selected.id, s)} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
